import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from 'react-router-dom'
import DashboardLayout from '@/pages/Layout'
import TopicListPage from '@/pages/topic'
import ModernDiscoverPage from '@/pages/topic/ModernDiscoverPage'
import DiscoverHome from '@/pages/topic/DiscoverHome'
import ModernWorkspace from '@/pages/home/ModernWorkspace'
import CleanWorkspace from '@/pages/home/CleanWorkspace'
import ConnectionsPage from '@/pages/connections'
import CirclesPage from '@/pages/circles/Circles'
import UserProfile from '@/pages/user-profile'
import MyProfile from '@/pages/my-profile'
import ModernProfilePage from '@/pages/my-profile/ModernProfilePage'
import EditTopic from '@/pages/topic-management/Edit'
import CreateTopic from '@/pages/topic-management/Create'
import ShareInteractionsPage from '@/pages/share'
import SearchResult from '@/pages/search'
import NotificationPage from '@/pages/notifications'
import UploadsPage from '@/pages/uploads'
import PatternLayout from '@/pages/pattern/Layout'
import PatternPage from '@/pages/pattern'
import GuestLandingLayout from '@/pages/guest-landing/Layout'
import TopicDetailsLayout from '@/pages/topic-details/Layout'
import { TopicPreviewBase } from '@/pages/topic-details/TopicPreviewBase'
import { PrivacyType, TopicType } from '@/utils/constants'
import { useSubscription } from '@/context/SubscriptionContext'
import { useEffect } from 'react'
import { FEATURE_KEYS } from '@/utils/subscriptions'
import MyConnectionsPage from '@/pages/my-profile/MyConnectionsPage'
import { DISCOVER_TABS, WORKSPACE_TABS } from '@/utils/homePageFilterTabs'
import NotFoundPage from '@/pages/errors/NotFoundPage'
import ErrorPage from '@/pages/errors/ErrorPage'

const AlbumProGuard = ({ isDiscover = false }) => {
  const { showPaywall, isFeatureAvailable } = useSubscription()

  const available = isFeatureAvailable(FEATURE_KEYS.COLLECTIONS)

  useEffect(() => {
    if (!available) showPaywall()
  }, [available])

  if (!available) return <Navigate to='/' replace />

  return isDiscover ? (
    <DiscoverHome topicType={TopicType.ALBUM} privacyType={PrivacyType.PUBLIC} />
  ) : (
    <TopicListPage topicType={TopicType.ALBUM} privacyType={PrivacyType.PRIVATE} />
  )
}

export const RootRouter = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path='/' element={<GuestLandingLayout />} errorElement={<ErrorPage />}>
        <Route path='auth' />
      </Route>

      {/* Guest Pattern Editor - No auth required */}
      <Route path='/pattern' element={<PatternLayout />} errorElement={<ErrorPage />}>
        <Route path='' element={<PatternPage />} />
      </Route>

      {/* Public Preview - No auth required */}
      <Route path='/preview' element={<TopicDetailsLayout />} errorElement={<ErrorPage />}>
        <Route path='' element={<TopicPreviewBase />}>
          <Route path='pattern' element={<PatternPage />} />
        </Route>
      </Route>

      <Route
        path='/login'
        element={<Navigate to='/auth?step=login' replace />}
        errorElement={<ErrorPage />}
      />
      <Route
        path='/signup'
        element={<Navigate to='/auth?step=signup' replace />}
        errorElement={<ErrorPage />}
      />
      <Route
        path='/forgot-password'
        element={<Navigate to='/auth?step=forgot-password' replace />}
        errorElement={<ErrorPage />}
      />

      <Route path='/' element={<DashboardLayout />} errorElement={<ErrorPage />}>
        <Route element={<TopicListPage />} />
        <Route path='home' element={<TopicListPage />} />
        <Route path='topic' element={<TopicListPage />} />
        <Route path='topic/edit/:id' element={<EditTopic />} />
        <Route path='share' element={<ShareInteractionsPage />} />
        {/* <Route path='connections' element={<ConnectionsPage />} /> */}
        <Route path='topic/create' element={<CreateTopic />} />
        <Route path='circles' element={<CirclesPage />} />
        <Route path='uploads' element={<UploadsPage />} />

        <Route path='profile'>
          <Route path='me' element={<MyProfile />} />
          <Route path='me/connections' element={<MyConnectionsPage />} />
          <Route path='v2' element={<ModernProfilePage />} />
          <Route path='user' element={<UserProfile />} />
        </Route>

        <Route path='search' element={<SearchResult />} />
        <Route path='notification' element={<NotificationPage />} />

        {/* Authenticated Pattern Editor - inside DashboardLayout */}
        <Route path='editor/pattern' element={<PatternLayout />}>
          <Route path='' element={<PatternPage />} />
        </Route>
      </Route>

      <Route path='/discover' element={<DashboardLayout />} errorElement={<ErrorPage />}>
        <Route index element={<Navigate to='all' replace />} />
        <Route
          path='all'
          element={<DiscoverHome topicType={TopicType.ALL} privacyType={PrivacyType.PUBLIC} />}
        />
        <Route
          path='home'
          element={<DiscoverHome topicType={TopicType.ALL} privacyType={PrivacyType.PUBLIC} />}
        />

        {DISCOVER_TABS.map((tab) => (
          <Route
            path={tab.id}
            element={<DiscoverHome topicType={tab.value} privacyType={PrivacyType.PUBLIC} />}
          />
        ))}
        {/* <Route
          path='home'
          element={<DiscoverHome topicType={TopicType.ALL} privacyType={PrivacyType.PUBLIC} />}
        /> */}

        {/* <Route
          path='pattern'
          element={<DiscoverHome topicType={TopicType.PATTERN} privacyType={PrivacyType.PUBLIC} />}
        />
        <Route
          path='custom_print'
          element={
            <TopicListPage topicType={TopicType.CUSTOM_PRINTS} privacyType={PrivacyType.PUBLIC} />
          }
        />

        <Route
          path='quilt'
          element={<TopicListPage topicType={TopicType.QUILT} privacyType={PrivacyType.PUBLIC} />}
        />
        <Route path='album' element={<AlbumProGuard isDiscover />} />

        <Route
          path='gallery'
          element={<DiscoverHome topicType={TopicType.GALLERY} privacyType={PrivacyType.PUBLIC} />}
        /> */}
      </Route>

      <Route path='/workspace' element={<DashboardLayout />} errorElement={<ErrorPage />}>
        <Route index element={<Navigate to='all' replace />} />
        {/* <Route path='all' element={<CleanWorkspace />} /> */}
        {WORKSPACE_TABS.map((tab) => (
          <Route path={tab.id} element={<CleanWorkspace topicType={tab.value} />} />
        ))}

        {/* <Route path='album' element={<AlbumProGuard isDiscover={false} />} />
        <Route path='upload' element={<UploadsPage />} />
        <Route
          path='pattern'
          element={
            <TopicListPage topicType={TopicType.PATTERN} privacyType={PrivacyType.PRIVATE} />
          }
        />
        <Route
          path='custom_print'
          element={
            <TopicListPage topicType={TopicType.CUSTOM_PRINTS} privacyType={PrivacyType.PRIVATE} />
          }
        />
        <Route
          path='gallery'
          element={
            <TopicListPage topicType={TopicType.GALLERY} privacyType={PrivacyType.PRIVATE} />
          }
        /> */}
      </Route>

      {/* <Route path='/projects' element={<DashboardLayout />} >
        <Route
          path='quilt'
          element={<TopicListPage topicType={TopicType.QUILT} privacyType={PrivacyType.PRIVATE} />}
        />
        <Route
          path='gallery'
          element={
            <TopicListPage topicType={TopicType.GALLERY} privacyType={PrivacyType.PRIVATE} />
          }
        />
      </Route> */}

      {/* <Route path='/' element={<TopicDetailsLayout />}>
        <Route path='preview' element={<TopicPreviewBase />} />
      </Route>

      <Route path='/' element={<PatternLayout />}>
        <Route path='pattern' element={<PatternPage />} />
      </Route> */}
      <Route path='*' element={<NotFoundPage />} />
    </>,
  ),
)
